import type { GuideStepContent } from './phantomContent';

export const SOLFLARE_CONTENT = {
  title: 'Solflare wallet guide',
  lead:
    'Solflare is a popular Solana wallet for browser and mobile. Follow these steps to install it, connect to Canadawale Yar, and send or receive CWY.',
  steps: [
    {
      title: 'Get Solflare',
      phase: 'install',
      body: [
        'Open the official Solflare website. You will see Get extension for browsers and Download Solflare for mobile.',
        'Always start from solflare.com — do not install from random ads or email links.',
      ],
      link: {
        href: 'https://solflare.com',
        label: 'Open solflare.com',
      },
      image: {
        src: '/wallet_ui/solflare-homepage.jpg',
        alt: 'Solflare homepage with Get extension and Download Solflare buttons',
        caption: 'On solflare.com, click Get extension to install the browser wallet.',
      },
    },
    {
      title: 'Choose your browser',
      phase: 'install',
      body: [
        'On the Get Solflare extension page, pick your browser: Chrome, Firefox, Brave, Edge, Opera, or Arc.',
        'Click INSTALL under your browser to open the official store listing.',
      ],
      image: {
        src: '/wallet_ui/solflare-download-extension.jpg',
        alt: 'Solflare Get extension page listing Chrome, Firefox, Brave, Edge, Opera, and Arc',
        caption: 'Choose your browser on solflare.com/download and click INSTALL.',
      },
      link: {
        href: 'https://solflare.com/download',
        label: 'Open solflare.com/download',
      },
    },
    {
      title: 'Add Solflare from the Chrome Web Store',
      phase: 'install',
      body: [
        'On the Solflare Wallet store page, click Add to Chrome (or Add to Brave / Edge if that is your browser).',
        'Confirm Add extension, then pin Solflare in your toolbar so you can open it quickly.',
      ],
      image: {
        src: '/wallet_ui/solflare-chrome-store.jpg',
        alt: 'Chrome Web Store page for Solflare Wallet with Add to Chrome button',
        caption: 'Click Add to Chrome, then confirm to install the Solflare extension.',
      },
      tips: [
        'Create a new wallet or import an existing one, and save your recovery phrase offline.',
      ],
    },
    {
      title: 'Open My Crypto Wallet on CWY',
      phase: 'connect',
      body: [
        'Sign in to Canadawale Yar.',
        'Click your profile avatar in the top right, then choose My Crypto Wallet to open the Wallet page.',
      ],
      image: {
        src: '/wallet_ui/solflare-open-wallet-menu.jpg',
        alt: 'CWY profile menu showing My Account, My Crypto Wallet, and Sign out',
        caption: 'From the profile menu, open My Crypto Wallet to manage Solana wallets and CWY.',
      },
    },
    {
      title: 'Select Solflare on CWY',
      phase: 'connect',
      body: [
        'On the Wallet page, click Connect wallet (or open the wallet menu if another wallet is already linked).',
        'In Change wallet, choose Solflare and click Switch (or Connect).',
      ],
      image: {
        src: '/wallet_ui/solflare-change-wallet.jpg',
        alt: 'CWY Change wallet panel listing Phantom, Solflare, Backpack, Brave Wallet, and Nightly',
        caption: 'Select Solflare from the Change wallet list to link it to your CWY account.',
      },
      tips: [
        'If Solflare does not appear, make sure the extension is installed, then refresh the page.',
      ],
    },
    {
      title: 'Unlock Solflare',
      phase: 'connect',
      body: [
        'Solflare will ask you to Unlock your wallet.',
        'Enter your Solflare password and click Unlock. Approve any connect request for Canadawale Yar if prompted.',
      ],
      image: {
        src: '/wallet_ui/solflare-unlock.jpg',
        alt: 'Solflare Unlock your wallet screen with password field and Unlock button',
        caption: 'Enter your Solflare password and click Unlock to continue connecting.',
      },
      warnings: [
        'Never share your Solflare password or recovery phrase with anyone — CWY staff will never ask for them.',
      ],
    },
    {
      title: 'Receive CWY',
      phase: 'receive',
      body: [
        'Once Solflare is connected, click Receive on the Wallet page (Request CWY tokens).',
        'Confirm your receiving address shows via Solflare, choose the amount (minimum 1 CWY), then click Request CWY.',
        'Depending on program rules, a request may need approval before CWY is sent to your Solflare wallet.',
      ],
      image: {
        src: '/wallet_ui/solflare-receive-cwy.jpg',
        alt: 'Receive CWY modal showing Solflare wallet address and Request CWY button',
        caption: 'Use Receive to request CWY into your connected Solflare wallet.',
      },
    },
    {
      title: 'Send CWY back to the vault',
      phase: 'send',
      body: [
        'To return CWY to the platform, click Send (Return CWY tokens / Add CWY to Your Account).',
        'Choose how much to deposit, confirm the verified CWY destination address, then click Deposit to CWY and approve in Solflare when prompted.',
      ],
      image: {
        src: '/wallet_ui/solflare-deposit-cwy.jpg',
        alt: 'Add CWY to Your Account modal with Solflare wallet, verified destination, and SOL fee warning',
        caption: 'Deposit CWY from Solflare to the verified CWY vault. Keep some SOL for network fees.',
      },
      warnings: [
        'Solana needs a small amount of SOL for network fees — CWY alone is not enough. If you see a fee warning, add at least ~0.001 SOL before depositing.',
      ],
    },
  ] satisfies GuideStepContent[],
} as const;
